import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { httpsCallable } from 'firebase/functions';
import { FirebaseContext } from 'context';
import firebaseConfig from '../../../firebase/config';
import InfoTooltip from '../../Shared/InfoTooltip';

const { databaseId } = firebaseConfig;

function SentimentAnalysisTrigger({
  sessionId,
  hasSentimentData,
  onAnalysisComplete
}) {
  const { firebase } = useContext(FirebaseContext);

  const [isAnalysing, setIsAnalysing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isWaitingForRefresh, setIsWaitingForRefresh] = useState(false);

  const analyseTooltipContent =
    "Generate AI-powered sentiment analysis using OpenAI's GPT-5. This will analyse all comments and replies to determine positive, neutral, or negative sentiment.";

  const reanalyseTooltipContent =
    'Update the GPT-5-powered sentiment analysis with any new comments that have been added since the last analysis.';

  // Reset waiting state when parent data updates
  useEffect(() => {
    if (hasSentimentData && isWaitingForRefresh) {
      setIsWaitingForRefresh(false);
    }
  }, [hasSentimentData, isWaitingForRefresh]);

  const handleAnalyse = async () => {
    setIsAnalysing(true);
    setError(null);
    setResult(null);

    try {
      const analyseSentiment = httpsCallable(
        firebase.functions,
        'analytics-analyseSentiment'
      );

      const response = await analyseSentiment({
        sessionId,
        forceReanalyse: hasSentimentData,
        databaseId
      });

      setResult(response.data);

      // Set waiting state before triggering parent refresh
      setIsWaitingForRefresh(true);

      // Notify parent component to refresh analytics data
      if (onAnalysisComplete) {
        onAnalysisComplete();
      }
    } catch (err) {
      console.error('Sentiment analysis error:', err);
      setError(err.message || 'Failed to analyse sentiment');
    } finally {
      setIsAnalysing(false);
    }
  };

  return (
    <Container>
      <ButtonWrapper>
        {isAnalysing || isWaitingForRefresh ? (
          <StatusMessage>
            <LoadingSpinner />
            <span>
              {isWaitingForRefresh
                ? 'Loading updated analytics...'
                : 'Analysing comment sentiment with GPT-5...'}
            </span>
          </StatusMessage>
        ) : (
          <>
            <TriggerButton onClick={handleAnalyse} disabled={isAnalysing}>
              {hasSentimentData ? 'Reanalyse Sentiment' : 'Analyse Sentiment'}
            </TriggerButton>
            <InfoTooltip
              content={
                hasSentimentData
                  ? reanalyseTooltipContent
                  : analyseTooltipContent
              }
            />
          </>
        )}
      </ButtonWrapper>

      {result && hasSentimentData && (
        <ResultMessage $success>
          <ResultTitle>✓ Analysis Complete</ResultTitle>
          <ResultDetails>
            <div>
              <strong>Analysed:</strong> {result.results.analysedComments ?? 0}{' '}
              comments
            </div>
            <div>
              <strong>Skipped:</strong> {result.results.skippedComments}{' '}
              (already analysed)
            </div>
            {result.results.errors > 0 && (
              <div>
                <strong>Errors:</strong> {result.results.errors}
              </div>
            )}
            {/* <div>
              <strong>Estimated Cost:</strong> $
              {result.results.estimatedCost.toFixed(4)}
            </div> */}
          </ResultDetails>
        </ResultMessage>
      )}

      {error && (
        <ResultMessage $error>
          <ResultTitle>✗ Analysis Failed</ResultTitle>
          <p>{error}</p>
        </ResultMessage>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ButtonWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.125rem;
`;

const TriggerButton = styled.button`
  background: #3b82f6;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StatusMessage = styled.div`
  align-items: center;
  color: var(--light-brown);
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  gap: 0.75rem;
  height: 40px;
`;

const LoadingSpinner = styled.div`
  animation: spin 1s linear infinite;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  border-top-color: #3b82f6;
  height: 20px;
  width: 20px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ResultMessage = styled.div`
  background: ${(props) =>
    props.$success ? '#f0fdf4' : props.$error ? '#fef2f2' : '#f9fafb'};
  border: 1px solid
    ${(props) =>
      props.$success ? '#86efac' : props.$error ? '#fca5a5' : '#e5e7eb'};
  border-radius: 6px;
  color: ${(props) =>
    props.$success
      ? '#166534'
      : props.$error
        ? '#991b1b'
        : 'var(--light-brown)'};
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  padding: 1rem;
`;

const ResultTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ResultDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export default SentimentAnalysisTrigger;
